/* eslint-disable */
import { connect } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useMemo, useState } from 'react';
import { Package, Calendar as CalendarIcon } from 'lucide-react';
import DatePicker from 'rsuite/DatePicker';
import { modalActions } from '../../../Actions';
import { BaseModal } from '../BaseModal';
import styles from './FileUploaderModal.module.scss';
import DragDropUploader from '../../../Pages/Analytics/Inventory Analytics/UploadPurchaseOrder/DragDropUploader/DragDropUploader';
import { Button } from '@/Components/ui/button';
import RenderItemsTable from './RenderItemsTable';

const FileUploaderModal = (props) => {
	const { show, toggleModal, data } = props;
	const HEADING = `${data?.heading || 'Upload Document'}`;
	const [uploadedFile, setUploadedFile] = useState('');
	const [poDId, setPoDId] = useState('');
	const [poDIdError, setPoDIdError] = useState('');
	const [localItems, setLocalItems] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);

	useEffect(() => {
		if (show) {
			setUploadedFile(data?.uploadedFile || '');
			setPoDId(data?.poDId || '');
			setLocalItems(data?.items || []);
			setSelectedDate(null);
		}
	}, [show, data]);

	const handleClose = () => {
		toggleModal(false);
		setUploadedFile('');
		setPoDId('');
		setPoDIdError('');
		setLocalItems([]);
	};

	const handleItemsChange = (newItems) => {
		setLocalItems(newItems);
	};

	const handleConfirm = () => {
		if (data?.inputInfo?.mandatory && !poDId?.trim()) {
			setPoDIdError(`${data?.inputInfo?.label} is required`);
			return;
		}
		setPoDIdError('');
		const receivedQtys = {};
		localItems.forEach(item => {
			const key = item.variantId;
			receivedQtys[key] = item.receivedQty;
		});

		if (data?.handleConfirm) {
			handleClose();
			data?.handleConfirm(uploadedFile, data?.id, poDId, receivedQtys, selectedDate);
			return;
		}
		props?.handleConfirm?.(uploadedFile, data?.id, poDId, receivedQtys, selectedDate);
	};

	const showItemsTable = data?.showItemsTable;
	const tableColumns = data?.tableColumns || [];
	const tableColMapping = data?.tableColMapping || {};

	const firstEditableColumnKey = useMemo(
		() => tableColumns.find((c) => c.editable)?.key,
		[tableColumns]
	);

	const confirmedField = data?.confirmedField
		?? (showItemsTable
			? (tableColMapping[firstEditableColumnKey] || firstEditableColumnKey)
			: undefined);

	const isProceedDisabled = useMemo(() => {
		if (Boolean(data?.inputInfo?.mandatory) && !poDId?.trim()) return true;

		if (showItemsTable) {
			if (!uploadedFile) return true;
			if (!selectedDate) return true;

			const editableCols = tableColumns.filter((col) => col.editable);
			if (editableCols.length > 0) {
				const hasEmptyField = localItems.some((item) =>
					editableCols.some((col) => {
						const val = item[col.key];
						return val === undefined || val === null || val === '';
					})
				);
				if (hasEmptyField) return true;
			}
		}

		return false;
	}, [data, poDId, showItemsTable, selectedDate, uploadedFile, tableColumns, localItems]);


	const buttons = () => {
		if (showItemsTable) {
			return (
				<div className='flex items-center justify-end w-full'>
					<div className='flex items-center gap-2'>
						<Button onClick={handleClose} variant='outline'>
							{data?.cancelText ?? 'Cancel'}
						</Button>
						<Button
							onClick={handleConfirm}
							className='primary-btn'
							disabled={isProceedDisabled}
						>
							<CheckIcon fontSize='small' />
							{data?.confirmText ?? 'Proceed'}
						</Button>
					</div>
				</div>
			);
		}
		return (
			<div className={styles['btn-container']}>
				<Button onClick={handleClose} variant='destructive' className='flex-1'>
					<ClearIcon />
					{data?.cancelText ?? 'Cancel'}
				</Button>
				<Button
					onClick={handleConfirm}
					className='primary-btn flex-1'
					disabled={isProceedDisabled}
				>
					<CheckIcon />
					{data?.confirmText ?? 'Proceed'}
				</Button>
			</div>
		);
	};

	const tableHeading = data?.tableHeading ?? 'Confirm Received Quantities';

	const customHeading = showItemsTable
		? () => (
			<div className='w-full flex items-center justify-start gap-2 p-1 mb-2'>
				<Package size={20} className='text-gray-700' />
				<span className='text-lg font-semibold text-gray-900'>{HEADING}</span>
			</div>
		)
		: undefined;

	return (
		<BaseModal
			heading={showItemsTable ? undefined : HEADING}
			customHeading={customHeading}
			height={showItemsTable ? '88vh' : '36rem'}
			renderBtns={buttons}
			open={show}
			handleClose={handleClose}
			width={showItemsTable ? '90vw' : '50rem'}
			wrapperClassName={showItemsTable ? 'flex flex-col !overflow-hidden' : ''}
			customStyles={showItemsTable ? { overflow: 'hidden' } : {}}
		>
			{data?.info && (
				<div className='flex flex-wrap items-center justify-center gap-x-8 gap-y-2 w-fit mx-auto mb-4 p-2 px-6 bg-slate-50 rounded-lg border border-slate-200'>
					{data.info.map((item, index) => (
						<div key={index} className='flex flex-col'>
							<span className='text-[10px] uppercase font-bold text-slate-500 tracking-wider'>{item.label}</span>
							<span className='text-sm font-semibold text-slate-900'>{item.value}</span>
						</div>
					))}
				</div>
			)}
			{showItemsTable ? (
				<div className='flex flex-col h-full min-h-0 gap-3 text-left'>
					{/* Full-width POD ID input */}
					{data?.showInput && (
						<div className='flex gap-4 items-end'>
							<div className='flex-1'>
								<label className='block text-sm font-semibold text-gray-800 mb-1'>
									{data?.inputInfo?.label}
									{data?.inputInfo?.mandatory && (
										<span className='text-red-500 ml-0.5'>*</span>
									)}
								</label>
								<input
									type='text'
									value={poDId}
									onChange={(e) => {
										setPoDId(e.target.value);
										if (poDIdError) setPoDIdError('');
									}}
									placeholder={data?.inputInfo?.placeholder}
									required={Boolean(data?.inputInfo?.mandatory)}
									className={`w-full rounded-md border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${poDIdError ? 'border-red-500' : 'border-gray-300'}`}
								/>

								{poDIdError && (
									<p className='mt-1 text-xs text-red-600'>{poDIdError}</p>
								)}
							</div>

							<div className='flex-1'>
								<label className='block text-sm font-semibold text-gray-800 mb-1'>
									Receiving Date
									<span className='text-red-500 ml-0.5'>*</span>
								</label>
								<DatePicker
									value={selectedDate}
									onChange={setSelectedDate}
									placeholder='Select Receiving Date'
									className='w-full'
									oneTap
									style={{ width: '100%' }}
								/>
							</div>
						</div>
					)}

					{/* Two-column: proof document (40%) + items table (60%) */}
					<div className='flex gap-4 flex-1 min-h-0'>
						<div className='flex flex-col basis-2/5 shrink-0 min-w-0 min-h-0'>
							<label className='block text-sm font-semibold text-gray-800 mb-2'>
								{data?.proofDocLabel ?? 'Proof Document'}
								<span className='text-red-500 ml-0.5'>*</span>
							</label>
							<div className='h-72'>
								<DragDropUploader
									uploadType='pdfFile'
									uploadedFile={uploadedFile}
									setUploadedFile={setUploadedFile}
									fileAcceptable='.pdf, application/pdf'
								/>
							</div>
						</div>

						<div className='flex flex-col basis-3/5 min-w-0 min-h-0 overflow-hidden'>
							<div className='flex items-center justify-between mb-2'>
								<label className='text-sm font-semibold text-gray-800'>
									{tableHeading}
									<span className='text-red-500 ml-0.5'>*</span>
								</label>

							</div>
							<div className='flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 shadow-sm'>
								<RenderItemsTable
									items={localItems}
									columns={tableColumns}
									colMapping={tableColMapping}
									onItemsChange={handleItemsChange}
									confirmedField={confirmedField}
									autoStepper={Boolean(showItemsTable)}
								/>
							</div>

						</div>
					</div>
				</div>
			) : (
				<>
					{data?.showInput && (
						<div className='mb-3'>
							<div className='flex items-center gap-3'>
								<label className='w-24 shrink-0 text-sm font-medium text-gray-700'>
									{data?.inputInfo?.label}
								</label>
								<input
									type='text'
									value={poDId}
									onChange={(e) => {
										setPoDId(e.target.value);
										if (poDIdError) setPoDIdError('');
									}}
									placeholder={data?.inputInfo?.placeholder}
									required={Boolean(data?.inputInfo?.mandatory)}
									className={`flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${poDIdError ? 'border-red-500' : 'border-gray-300'}`}
								/>
							</div>
							{poDIdError && (
								<p className='mt-1 text-xs text-red-600 pl-24'>{poDIdError}</p>
							)}
						</div>
					)}
					<div className='h-[377px]'>
						<DragDropUploader
							uploadType='pdfFile'
							uploadedFile={uploadedFile}
							setUploadedFile={setUploadedFile}
							fileAcceptable='.pdf, application/pdf'
						/>
					</div>
				</>
			)}
		</BaseModal>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state.modal.fileUploaderModal;
	return { show, data };
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleFileUploaderModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileUploaderModal);
